import { cacheKeys } from "@constants/cache-keys";
import { events } from "@constants/tracking";
import { usePracticeTests } from "@features/dashboard/hooks/usePracticeTests";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { retrieveExamContextFromKV } from "@features/exam/helpers";
import {
	type QueryClient,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

import type { ExamStatus } from "@memorang/types";
import { trackCustomEvent } from "analytics";
import {
	applyAccessCode,
	getActiveCodes,
} from "api/commerce/access-code-service";
import { router, useGlobalSearchParams } from "expo-router";
import { useEffect, useState } from "react";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { signInWithClerkTicket } from "@helpers/auth-provider";
import { getPlatform } from "@memorang/helpers/platform";
import { handleExternalAuth } from "api/commerce/auth-service";
import { fetchExamDetails, updateExamDetails } from "api/commerce/exam-service";
import type { ActiveCodesResponse } from "api/commerce/types";
import {
	examsListQueryOptions,
	refreshExamsListCache,
	setCachedExamDate,
} from "api/exams-query";
import { getMixedItemDistribution } from "api/queries";
import { deriveExternalAuthFlowStatus } from "./external-auth-flow-helpers";
import { useAppStore } from "./useAppStore";
import { useGlobalStore } from "./useGlobalStore";

const start_session = "true";
const auto_redeem = "true";

const handleClerkTicketSignIn = async (
	signInToken: string,
	email: string,
): Promise<string | null> => {
	const success = await signInWithClerkTicket(signInToken);
	return success ? email : null;
};

const findExistingExamContext = async (
	queryClient: QueryClient,
	userLogicalId: string,
) => {
	const cachedExamContext = await retrieveExamContextFromKV();
	if (cachedExamContext?.examId) {
		return cachedExamContext.examId;
	}

	const latestExams = await queryClient.fetchQuery(
		examsListQueryOptions(userLogicalId),
	);
	const purchasedExam = latestExams.find((item) =>
		item.exam.bundles.some(
			(bundle) => bundle.metadata?.subscription?.status === "ACTIVE",
		),
	);

	return purchasedExam?.exam.id;
};

const handleActiveCodesRedemption = async (
	activeCodesResult: ActiveCodesResponse[],
	auto_redeem: string,
	source: string,
) => {
	if (!activeCodesResult?.length) {
		return null;
	}

	if (auto_redeem !== "true") {
		return null;
	}

	const accessCode = activeCodesResult[0].codes[0];
	trackCustomEvent({
		eventName: events.promoCodeEntered,
		source,
		examId: accessCode,
	});

	const result = await applyAccessCode({ accessCode });

	if (!result.success) {
		return null;
	}

	const examId =
		typeof result.data.examId === "string"
			? result.data.examId
			: result.productData?.[0]?.examId;

	trackCustomEvent({
		eventName: events.promoCodeRedeemed,
		source,
		examId,
		examNumericId: result.data.examNumericId,
		code: accessCode,
	});

	return examId;
};

const useExternalAuthFlow = () => {
	const tenantId = useAppStore((state) => state.tenant.id);
	const [isAccessRestricted, setIsAccessRestricted] = useState(false);
	const setIsAuthFlowExamContextSwitching = useGlobalStore(
		(state) => state.setIsAuthFlowExamContextSwitching,
	);

	const { handleSwitchExamContext } = useExamContext();
	const queryClient = useQueryClient();

	const {
		userPublicMetadataId,
		userLogicalId,
		isLoading: authLoading,
	} = useAuthContext();

	const { handlePracticeTestSession, creatingSession } = usePracticeTests();

	const {
		exam_date,
		source,
		auth_token,
		sku: skuFromParams,
	} = useGlobalSearchParams<{
		start_session: string;
		auto_redeem: string;
		exam_date: string;
		source: string;
		auth_token: string;
		sku: string;
	}>();

	useEffect(() => {
		if (!auth_token) {
			return;
		}
		setIsAuthFlowExamContextSwitching(true);

		return () => {
			setIsAuthFlowExamContextSwitching(false);
		};
	}, [auth_token, setIsAuthFlowExamContextSwitching]);

	const {
		data: email,
		isLoading: isLoadingSession,
		isError: isErrorSession,
	} = useQuery({
		queryKey: ["session", auth_token],
		queryFn: async () => {
			const JWT_FORMAT = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
			if (!JWT_FORMAT.test(auth_token)) {
				return null;
			}
			const result = await handleExternalAuth({
				token: auth_token,
				platform: getPlatform(),
				tenantId,
			});
			const signInToken = result?.data.token;
			const resultEmail = result.data.email;

			if (!signInToken) {
				return null;
			}

			return handleClerkTicketSignIn(signInToken, resultEmail);
		},
		staleTime: 0,
		enabled: !!auth_token && !authLoading,
		gcTime: 0,
		retry: false,
	});

	const {
		data: examId,
		isLoading: isLoadingApplyAccessCode,
		isError: isErrorApplyAccessCode,
	} = useQuery({
		queryKey: ["redeem_access_code", tenantId, email, auth_token],
		queryFn: async () => {
			if (!(email && userLogicalId)) {
				return;
			}

			const activeCodesResult = await getActiveCodes({
				sku: skuFromParams,
			});

			const examIdFromRedemption = await handleActiveCodesRedemption(
				activeCodesResult,
				auto_redeem,
				source,
			);

			if (examIdFromRedemption) {
				await refreshExamsListCache(queryClient);
				return examIdFromRedemption;
			}

			if (skuFromParams) {
				const examDetails = await fetchExamDetails({
					sku: skuFromParams,
					examId: examIdFromRedemption!,
				});
				return examDetails.exam.id;
			}

			const existingExamId = await findExistingExamContext(
				queryClient,
				userLogicalId,
			);

			if (!existingExamId) {
				setIsAccessRestricted(true);
				return undefined;
			}

			return existingExamId;
		},
		enabled: !!tenantId && !!email && !!auth_token && !!userLogicalId,
		staleTime: 0,
		gcTime: 0,
		retry: false,
	});

	const { data: examDetails, isLoading: isLoadingExamDetails } = useQuery({
		queryKey: [cacheKeys.examDetails, examId, auth_token],
		queryFn: async () => {
			if (!examId) {
				return;
			}
			try {
				const result = await fetchExamDetails({
					examId,
				});
				await handleSwitchExamContext({
					...result,
					...(exam_date
						? {
								metadata: {
									examDate: exam_date,
									isActiveExam: true,
								},
							}
						: {}),
				});
				if (exam_date) {
					updateExamDetails({
						examId,
						metadata: {
							examDate: exam_date,
						},
					}).then(() => {
						if (userLogicalId) {
							setCachedExamDate(queryClient, userLogicalId, examId, exam_date);
						}
					});
				}
				return result;
			} finally {
				setIsAuthFlowExamContextSwitching(false);
			}
		},
		enabled: !!examId && !!auth_token,
		staleTime: 0,
		gcTime: 0,
	});

	const {
		isLoading: isLoadingExams,
		isSuccess: isExamsSuccess,
		isError: isErrorExams,
	} = useQuery({
		queryKey: [
			cacheKeys.externalAuthPracticeTest,
			examId,
			tenantId,
			auth_token,
		],
		queryFn: async () => {
			if (!(examDetails && userPublicMetadataId)) {
				return null;
			}

			const result = await getMixedItemDistribution({
				userPublicMetadataId,
				action: "GET_PRATICETEST_DISTRIBUTION",
				examId: examId!,
			});
			const [firstExam] =
				result.exams?.filter(
					(exam) => exam.subType === "PRACTICE_TEST" && exam.hasAccess,
				) || [];

			if (!firstExam) {
				setIsAccessRestricted(true);
				return null;
			}
			if (start_session === "true") {
				await handlePracticeTestSession(
					firstExam.id,
					firstExam.status as ExamStatus,
					firstExam.numericId!,
					false,
					true,
					false,
					firstExam.collectionId,
					true,
				);
			} else {
				router.push("/practice-tests");
			}
			return result;
		},
		enabled: !!examDetails && !!auth_token,
		staleTime: 0,
		gcTime: 0,
	});

	useEffect(() => {
		if (isErrorSession || isErrorApplyAccessCode || isAccessRestricted) {
			setIsAuthFlowExamContextSwitching(false);
		}
	}, [
		isErrorSession,
		isErrorApplyAccessCode,
		isAccessRestricted,
		setIsAuthFlowExamContextSwitching,
	]);

	const { messages, isLoading, showError } = deriveExternalAuthFlowStatus({
		hasAuthToken: !!auth_token,
		hasEmail: !!email,
		hasExamId: !!examId,
		hasExamDetails: !!examDetails,
		isAccessRestricted,
		creatingSession,
		isLoadingSession,
		isErrorSession,
		isLoadingApplyAccessCode,
		isErrorApplyAccessCode,
		isLoadingExamDetails,
		isLoadingExams,
		isExamsSuccess,
		isErrorExams,
	});
	return {
		creatingSession,
		isLoading,
		messages,
		showError,
		email,
		isAccessRestricted,
		setIsAccessRestricted,
	};
};

export default useExternalAuthFlow;
